import { Action, createReducer, on } from '@ngrx/store';

import { DEFAULT_RESEARCH_STUDY_STATUS_PARAM } from '@app/lib/constants/http.constants';
import { IListNavigator } from '@app/lib/interfaces';
import { IResearchStudyDisplay, ITherapeuticArea } from 'projects/labcorp/clinical-trials-ui/src/lib/interfaces';
import { MANAGE_STUDIES_FILTER_OPTIONS } from '../studies.constants';
import * as StudiesActions from './studies.actions';
import * as utils from '@app/lib/utils';

export interface IStudiesState {
  allOrganizations?: fhir4.Organization[];
  researchStudies?: IResearchStudyDisplay[];
  researchStudyToEdit?: IResearchStudyDisplay;
  studyListNavigator?: IListNavigator;
  therapeuticAreas?: ITherapeuticArea[];
}

export const initialState: IStudiesState = {
  allOrganizations: null,
  researchStudies: null,
  researchStudyToEdit: null,
  studyListNavigator: {
    filterListOptions: MANAGE_STUDIES_FILTER_OPTIONS,
    filterParams: {
      ...DEFAULT_RESEARCH_STUDY_STATUS_PARAM
    }
  },
  therapeuticAreas: null
};

// tslint:disable-next-line:typedef
export function studiesReducer(existingState: IStudiesState, action: Action) {
  return createReducer(
    initialState,
    on(StudiesActions.clearResearchStudyState, (state) => {
      return {
        ...initialState
      };
    }),
    on(StudiesActions.clearResearchStudyToEdit, (state) => ({
        ...state,
        researchStudyToEdit: null
    })),
    on(StudiesActions.getResearchStudies, (state) => {
      const updatedListNavigator = utils.clone<IListNavigator>(state.studyListNavigator);
      updatedListNavigator.processing = true;

      return {
        ...state,
        studyListNavigator: updatedListNavigator
      };
    }),
    on(StudiesActions.getResearchStudiesSuccess, (state, { researchStudies, paginationOptions, total }) => {
      const updatedStudyListNavigator = utils.updateListNavigator(state.studyListNavigator, paginationOptions, total);
      updatedStudyListNavigator.filterListOptions = state.studyListNavigator.filterListOptions;
      updatedStudyListNavigator.filterParams = state.studyListNavigator.filterParams;
      updatedStudyListNavigator.processing = false;
      updatedStudyListNavigator.reload = false;
      updatedStudyListNavigator.resultsStartValue = (updatedStudyListNavigator.pager.currentPageIndex * updatedStudyListNavigator.pager.perPage) + 1;
      updatedStudyListNavigator.resultsEndValue = updatedStudyListNavigator.resultsStartValue + researchStudies.length - 1;

      return {
        ...state,
        researchStudies,
        studyListNavigator: updatedStudyListNavigator,
        researchStudyToEdit: null
      };
    }),
    on(StudiesActions.getTherapeuticAreasSuccess, (state, { therapeuticAreas: successTherapeuticAreas }) => ({
      ...state,
      therapeuticAreas: successTherapeuticAreas
    })),
    on(StudiesActions.markResearchStudyToEditSuccess, (state, { researchStudy }) => ({
        ...state,
        researchStudyToEdit: researchStudy
    })),
    on(StudiesActions.setPageIndex, (state, { index }) => {
      const updatedListNavigator = utils.clone<IListNavigator>(state.studyListNavigator);
      updatedListNavigator.pager.currentPageIndex = index;
      updatedListNavigator.pager.currentPage = index + 1;
      updatedListNavigator.reload = true;
      updatedListNavigator.processing = true;

      return {
        ...state,
        studyListNavigator: updatedListNavigator
      };
    }),
    on(StudiesActions.updateFilterParams, (state, { filterParam }) => {
      const updatedListNavigator = utils.clone<IListNavigator>(state.studyListNavigator);

      if (filterParam.value) {
        updatedListNavigator.filterParams[filterParam.paramName] = filterParam.value;
      }
      else {
        delete updatedListNavigator.filterParams[filterParam.paramName];
      }

      return {
        ...state,
        studyListNavigator: updatedListNavigator
      };
    })
  )(existingState, action);
}

export const organizations = (state: IStudiesState) => state.allOrganizations;
export const researchStudyToEdit = (state: IStudiesState) => state.researchStudyToEdit;
export const studies = (state: IStudiesState) => state.researchStudies;
export const studyListNavigator = (state: IStudiesState) => state.studyListNavigator;
export const therapeuticAreas = (state: IStudiesState) => state.therapeuticAreas;
